import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getApps, initializeApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';

type AppUser = {
  id: string;
  name: string;
  email: string;
  photoURL?: string;
  isAdmin: boolean;
  isDemo: boolean;
};

type AuthValue = {
  user: AppUser | null;
  loading: boolean;
  configured: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | undefined>;
};

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const configured = Boolean(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId);
const firebaseApp = configured ? (getApps()[0] ?? initializeApp(firebaseConfig)) : null;
const auth = firebaseApp ? getAuth(firebaseApp) : null;
const adminEmails = new Set(['mrsuntsu@gmail.com']);

function mapUser(user: User, isAdmin = false): AppUser {
  return {
    id: user.uid,
    name: user.displayName || user.email?.split('@')[0] || 'Candidate',
    email: user.email || '',
    photoURL: user.photoURL || undefined,
    isAdmin: isAdmin || (user.emailVerified && adminEmails.has(user.email?.toLowerCase() || '')),
    isDemo: false,
  };
}

const demoUser: AppUser = {
  id: 'local-demo',
  name: 'Alex',
  email: 'demo@roundjump.local',
  isAdmin: true,
  isDemo: true,
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(configured ? null : demoUser);
  const [loading, setLoading] = useState(configured);

  useEffect(() => {
    if (!auth) return;
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
      } else {
        const claims = await firebaseUser.getIdTokenResult();
        setUser(mapUser(firebaseUser, claims.claims.admin === true));
      }
      setLoading(false);
    });
  }, []);

  const value: AuthValue = {
    user,
    loading,
    configured,
    signInWithGoogle: async () => {
      if (!auth) return;
      await signInWithPopup(auth, new GoogleAuthProvider());
    },
    signInWithEmail: async (email, password) => {
      if (!auth) return;
      await signInWithEmailAndPassword(auth, email, password);
    },
    signUpWithEmail: async (email, password) => {
      if (!auth) return;
      await createUserWithEmailAndPassword(auth, email, password);
    },
    signOut: async () => {
      if (!auth) return;
      await firebaseSignOut(auth);
    },
    getToken: async () => auth?.currentUser?.getIdToken(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
